import {PayloadAction, createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import axios from "axios";
import {BASE_URL} from "../config";
import { ICounsellingSessionForm, ICounsellingSessionSlot, ISlotGroupedByDate } from "../interfaces/CounsellingSlot";
import moment from "moment";
import { toast } from "react-toastify";

interface IFetchCounselling {
    programId: string;
}
interface ProgramCounsellingState {
    loading: boolean;
    error: string | null;
    programCounselling: ISlotGroupedByDate[] | [];
}

const initialState: ProgramCounsellingState = {
    loading: false,
    error: null,
    programCounselling: [],
};

interface IDeleteCounselling {
    counsellingID: string
}


/**
 * Axios request to fetch questions for a given programId
 */
export const fetchCounselling = createAsyncThunk('counselling/fetchCounselling',
    async ({programId}: IFetchCounselling, thunkAPI) => {       
        try {
            const accessToken = JSON.parse(localStorage.getItem('loginData') as string)?.accessToken;

            if (!accessToken) {
                console.error('Access token not found in the response.');
                return {}; // or any default value if needed
            }
            const response = await axios(`${BASE_URL}/api/v1/programs/${programId}/counsellings`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Add other headers if needed
                },
            });
            const responseData = response?.data
            if (response.status !== 200 && response.statusText !== 'OK') {
                throw new Error(`Request failed with status ${response?.data?.message}`);
            }
            return responseData?.data?.counselingSessions || [];
        } catch (error: any) {
            console.error('Error:', error.message);
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);


/**
 * AXIOS request for store Counselling
 */
export const storeCounselling = createAsyncThunk('counselling/storeCounselling',
async ({programId,startTime,endTime,duration,selectedDates,zoomLink }: ICounsellingSessionForm, thunkAPI) => {
    try {
        const accessToken = JSON.parse(localStorage.getItem('loginData') as string)?.accessToken;

        if (!accessToken) {
            console.error('Access token not found in the response.');
            return {}; // or any default value if needed
        }
        const response = await axios.post(`${BASE_URL}/api/v1/counsellings`, {
            programId,
            startTime: moment(startTime).format('YYYY-MM-DD hh:mm A'),
            endTime: moment(endTime).format('YYYY-MM-DD hh:mm A'),
            duration,
            selectedDates,
            zoomLink
            }, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Add other headers if needed
                },
            });
            const responseData = response?.data
            if (response.status !== 201 && response.statusText !== 'OK' || response.status === 422) {
                toast.error("Slots are not created, please try again");
                throw new Error(`Request failed with status ${response?.data?.message}`);
            }
            toast.success("Slots Created successfully");
            thunkAPI.dispatch(fetchCounselling({programId}));
            return responseData?.data || [];
        } catch (error: any) {
            console.error('Error:', error.message);
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

/**
 * Axios API request to delete a counselling
 */
export const deleteCounselling = createAsyncThunk('counselling/deleteCounselling',
    async ({ counsellingID }: IDeleteCounselling, thunkAPI) => {
        try {
            const accessToken = JSON.parse(localStorage.getItem('loginData') as string)?.accessToken;

            if (!accessToken) {
                console.error('Access token not found in the response.');
                return {}; // or any default value if needed
            }
            const response = await axios.delete(`${BASE_URL}/api/v1/counsellings/${counsellingID}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Add other headers if needed
                },
            });
            const responseData = response?.data
            if (response.status !== 200 && response.statusText !== 'OK' || response.status === 422) {
                throw new Error(`Request failed with status ${response?.data?.message}`);
            }else{
                return responseData?.data?.questions || [];    
            }                    
        } catch (error: any) {
            console.error('Error:', error.message);
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

const programCounsellingSlice = createSlice({
    name: "programCounselling",
    initialState,
    reducers: {
        resetState: (state) => initialState
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCounselling.pending, (state) => {
            state.loading = true;
        })
        .addCase(fetchCounselling.fulfilled, (state, action) => {
            state.loading = false;
            state.programCounselling = action.payload;
        })
        .addCase(fetchCounselling.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message ?? 'An error occurred';
        });
    },
});

export const { resetState } = programCounsellingSlice.actions;
export default programCounsellingSlice.reducer;